/**
 * Напоминания о приёме лекарств.
 *
 * Планировщик уже разложил курс на конкретные дозы — остаётся сообщить о них
 * системе уведомлений. Модуль сознательно тонкий: он не запрашивает разрешение
 * и ничего не показывает сам. Если уведомления выключены, вызовы просто ничего
 * не делают, и таблетница продолжает работать как журнал.
 */

import { expectedDoseMin, isDoseAhead } from '@/entities/medication/model/dose-status';
import {
  describeMealRelation,
  formatMedicationDose,
} from '@/entities/medication/model/medication-meta';
import { cancel, schedule } from '@/shared/notifications/local-notifications';
import type { DoseEvent, Medication } from '@/shared/db/schema';
import { type IsoDate, type MinutesOfDay, toIsoDate } from '@/shared/lib/datetime';

/**
 * Когда напоминать: отложенный приём ждёт нового времени, а не отмены.
 *
 * До этой функции напоминание снималось при любом статусе, кроме `pending`, —
 * то есть кнопка «Отложить на 15 минут» не переносила уведомление, а отменяла
 * его навсегда. Человек откладывал приём именно потому, что хотел, чтобы ему
 * напомнили ещё раз.
 */
function remindAt(dose: DoseEvent): MinutesOfDay | null {
  if (!isDoseAhead(dose)) return null;

  // Отложили за полночь — этот день уже не про него. Общее правило в такой
  // ситуации возвращается к плановому времени: экрану дня показать приём надо
  // хоть где-то. Напоминанию — нет: звать к таблетке в час, от которого
  // человек её и отодвинул, значит не выполнить его просьбу.
  if (dose.status === 'postponed' && dose.snoozedUntil !== null) {
    if (toIsoDate(new Date(dose.snoozedUntil)) !== dose.date) return null;
  }

  return expectedDoseMin(dose);
}

/** Идентификатор напоминания привязан к дозе — перенос заменяет, а не дублирует. */
function reminderId(dose: DoseEvent): string {
  return `dose-${dose.id}`;
}

/**
 * Текст уведомления о приёме.
 *
 * Уведомление приходит на экран блокировки: на стол, в руки соседа по вагону,
 * на общий монитор во время созвона. Название лекарства там — это диагноз,
 * прочитанный вслух, поэтому по умолчанию его не видно.
 *
 * Связь с едой остаётся в обоих случаях: «после завтрака» говорит о
 * распорядке, а не о болезни, и без неё напоминание теряет половину смысла.
 */
export function doseNotificationText(
  medication: Medication,
  showName: boolean,
): { title: string; body: string } {
  const mealText = describeMealRelation(medication.mealRelation, medication.mealOffsetMin);

  if (!showName) {
    return {
      title: 'Пора принять лекарство',
      // Ни названия, ни дозировки: «50 мкг» в паре со временем сужает круг
      // догадок ничуть не хуже названия.
      body: mealText || 'Откройте таблетницу, чтобы увидеть какое',
    };
  }

  return {
    title: `Пора принять: ${medication.name}`,
    body: mealText
      ? `${formatMedicationDose(medication)} · ${mealText}`
      : formatMedicationDose(medication),
  };
}

/**
 * Приводит набор напоминаний в соответствие с журналом приёмов.
 *
 * Отмеченные дозы снимаются с напоминания сразу: уведомление «пора принять» о
 * том, что уже принято, обесценивает все остальные.
 */
export function syncDoseReminders(
  doses: DoseEvent[],
  medications: Medication[],
  today: IsoDate,
  showNames = false,
): void {
  const byId = new Map(medications.map((medication) => [medication.id, medication]));

  for (const dose of doses) {
    if (dose.date !== today) continue;

    const medication = byId.get(dose.medicationId);
    if (!medication || dose.deletedAt !== null) {
      void cancel(reminderId(dose));
      continue;
    }

    const timeMin = remindAt(dose);
    if (timeMin === null) {
      void cancel(reminderId(dose));
      continue;
    }

    void schedule({
      id: reminderId(dose),
      ...doseNotificationText(medication, showNames),
      category: 'medication',
      date: dose.date,
      timeMin,
      route: '/health',
      // По этой ссылке кнопка «Принял» отмечает приём, не открывая экран.
      target: { kind: 'dose', id: dose.id, date: dose.date },
    });
  }
}
